import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import API, { fileUrl, downloadFile } from '../utils/api';
import { FRONTEND_TASKS, FRONTEND_MODULES } from '../microcontent/frontendcontent';
import { PYTHON_TASKS, PYTHON_MODULES } from '../microcontent/pythoncontent';
import { JAVA_FULLSTACK_TASKS, JAVA_FULLSTACK_MODULES } from '../microcontent/javafullstackcontent';
import { MERN_TASKS, MERN_MODULES } from '../microcontent/mernstackcontent';
import { CLOUD_DEV_TASKS, CLOUD_DEV_MODULES } from '../microcontent/clouddevelopmentcontent';
import { BACKEND_NODE_TASKS, BACKEND_NODE_MODULES } from '../microcontent/backendnodecontent';
import { UIUX_TASKS, UIUX_MODULES } from '../microcontent/uiuxdesigncontent';
import { PYTHON_DS_TASKS, PYTHON_DS_MODULES } from '../microcontent/pythondatasciencecontent';
import { MOBILE_DEV_TASKS, MOBILE_DEV_MODULES } from '../microcontent/mobiledevelopmentcontent';
import { DEVOPS_TASKS, DEVOPS_MODULES } from '../microcontent/devopscontent';

/* ─────────────────────────────────────────────────────────────────
   COURSE TASK CONTENT
───────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────
   MODULE MAP — maps course names to their module structures
   Add other courses here as you create their content files.
───────────────────────────────────────────────────────────────── */
const MODULE_COURSE_MAP = {
  "Frontend Web Development": FRONTEND_MODULES,
  "Python Programming": PYTHON_MODULES,
  "Java Full Stack Development": JAVA_FULLSTACK_MODULES,
  "MERN Stack Development": MERN_MODULES,
  "Cloud Development": CLOUD_DEV_MODULES,
  "Backend Node.js Development": BACKEND_NODE_MODULES,
  "UI/UX Design": UIUX_MODULES,
  "Python Data Science": PYTHON_DS_MODULES,
  "Mobile App Development": MOBILE_DEV_MODULES,
  "DevOps": DEVOPS_MODULES,
};

const COURSE_TASKS = {
  "Frontend Web Development": FRONTEND_TASKS,
  "Python Programming": PYTHON_TASKS,
  "Java Full Stack Development": JAVA_FULLSTACK_TASKS,
  "MERN Stack Development": MERN_TASKS,
  "Cloud Development": CLOUD_DEV_TASKS,
  "Backend Node.js Development": BACKEND_NODE_TASKS,
  "UI/UX Design": UIUX_TASKS,
  "Python Data Science": PYTHON_DS_TASKS,
  "Mobile App Development": MOBILE_DEV_TASKS,
  "DevOps": DEVOPS_TASKS,
};

/* ─────────────────────────────────────────────────────────────────
   STATUS CONFIG
───────────────────────────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending_payment: { label: "Payment Verification Pending", color: "#F59E0B", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.3)", emoji: "⏳", motivation: "Hang tight! Our team is reviewing your payment.", sub: "Verification usually takes 1–2 hours." },
  in_progress:     { label: "Tasks Unlocked — In Progress",  color: "var(--v1)", bg: "rgba(127,0,255,0.07)", border: "rgba(127,0,255,0.25)", emoji: "🚀", motivation: "Work through the topics, complete each one, then submit when ready.", sub: "Take your time — quality matters more than speed." },
  under_review:    { label: "Submitted — Under Review",      color: "#3B82F6", bg: "rgba(59,130,246,0.07)", border: "rgba(59,130,246,0.25)", emoji: "🔍", motivation: "Great work submitting! Our team is reviewing your code.", sub: "Reviews usually take 24–48 hours." },
  changes_requested: { label: "Changes Requested",          color: "#F87171", bg: "rgba(248,113,113,0.07)", border: "rgba(248,113,113,0.3)", emoji: "✏️", motivation: "Our team reviewed your submission and asked for a few changes.", sub: "Check the feedback below, fix it up, and resubmit." },
  completed:       { label: "Completed — Certificate Ready!", color: "#10B981", bg: "rgba(16,185,129,0.07)", border: "rgba(16,185,129,0.25)", emoji: "🏆", motivation: "Outstanding! Your code was approved.", sub: "Share your certificate on LinkedIn!" },
};

/* ─────────────────────────────────────────────────────────────────
   ICONS
───────────────────────────────────────────────────────────────── */
const ChevronIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);
const BookIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>);
const TaskIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>);
const ExternalIcon = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>);
const LockIcon = () => (<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>);
const CheckIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>);

/* ─────────────────────────────────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────────────────────────────────── */
const ProgressBar = ({ completed, total }) => {
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <div className="progress-bar-wrap">
      <div className="progress-bar-header">
        <span className="progress-bar-label">Overall Progress</span>
        <span className="progress-bar-count">{completed} / {total} tasks completed</span>
      </div>
      <div className="progress-bar-track">
        <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="progress-bar-pct">{pct}%</div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TOPIC ACCORDION CARD
───────────────────────────────────────────────────────────────── */
const TopicCard = ({ topic, index, status, totalTopics, isCompleted, githubLink, onLinkChange, onMarkComplete, onEditAgain, globalIndex, onShowGitPush, internshipId, email, initialScreenshots = [] }) => {
  const [open, setOpen] = useState(index === 0);
  const [screenshots, setScreenshots] = useState(() => (initialScreenshots || []).map(p => ({ url: fileUrl(p), name: p, uploading: false })) );   // { url, name } — local previews
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileInputRef = React.useRef();
  const isLocked = status === 'pending_payment';
  const alreadySubmitted = status === 'under_review' || status === 'completed';
  const submissionType = topic.submissionType || 'github'; // 'github' | 'screenshot' | 'both'
  const linkType = topic.linkType || 'github'; // 'github' | 'figma' — which kind of link this course collects
  const needsGithub = submissionType === 'github' || submissionType === 'both';
  const needsScreenshot = submissionType === 'screenshot' || submissionType === 'both';
  const hasValidGithub = githubLink.trim().includes(linkType === 'figma' ? 'figma.com' : 'github.com');
  const hasScreenshot = screenshots.some(s => !s.uploading);
  const canComplete = (!needsGithub || hasValidGithub) && (!needsScreenshot || hasScreenshot);

  const handleScreenshots = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Show local previews immediately
    const previews = files.map(f => ({ url: URL.createObjectURL(f), name: f.name, uploading: true }));
    setScreenshots(prev => [...prev, ...previews]);
    setUploading(true);

    try {
      const form = new FormData();
      form.append('email', email);
      form.append('taskIndex', String(index));
      form.append('topicName', topic.topic);
      files.forEach(f => form.append('screenshots', f));

      const res = await API.post(`/microinternships/upload-task-screenshot/${internshipId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Replace the uploading previews with server paths
      const serverPaths = res.data.paths || [];
      setScreenshots(prev => {
        const kept = prev.filter(s => !s.uploading);
        const uploaded = serverPaths.map(p => ({ url: fileUrl(p), name: p, uploading: false }));
        return [...kept, ...uploaded];
      });
    } catch {
      // Keep local previews if upload fails
      setScreenshots(prev => prev.map(s => ({ ...s, uploading: false })));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeScreenshot = (i) => {
    setScreenshots(prev => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className={`topic-card ${isCompleted ? 'topic-card-done' : ''}`} style={{ opacity: isLocked ? 0.45 : 1 }}>
      {/* Header */}
      <button className="topic-header" onClick={() => !isLocked && setOpen(o => !o)} disabled={isLocked}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={`topic-num ${isCompleted ? 'topic-num-done' : ''}`}>
            {isCompleted ? <CheckIcon /> : index + 1}
          </span>
          <div>
            <span className="topic-ttl">{topic.topic}</span>
            <span className="topic-meta">Task {index + 1} of {totalTopics}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isCompleted && <span className="badge-done">Completed ✓</span>}
          {isLocked && <span className="badge-locked">Locked</span>}
          <ChevronIcon open={open} />
        </div>
      </button>

      {/* Motivation banner when completed */}
      {isCompleted && (
        <div className="motivation-banner">
          <span style={{ fontSize: 20 }}>🎉</span>
          <p>{topic.motivation}</p>
        </div>
      )}

      {/* Body */}
      {open && (
        <div className="topic-body">

          {/* Learning Resource */}
          <div className="section-block">
            <div className="section-label"><BookIcon /><span>Learning Resource</span></div>
            <div className="resource-box">
              <p className="resource-name">{topic.resource.name}</p>
              <a href={topic.resource.url} target="_blank" rel="noopener noreferrer" className="open-btn">
                Open Resource <ExternalIcon />
              </a>
            </div>
          </div>

          {/* Task */}
          <div className="section-block">
            <div className="section-label"><TaskIcon /><span>Task</span></div>
            {topic.description && (
              <p className="topic-description">{topic.description}</p>
            )}
            <p className="task-desc">{topic.task}</p>
            <div className="req-box">
              <p className="req-head">Requirements</p>
              <ul className="req-list">
                {topic.requirements.map((r, i) => (
                  <li key={i} className="req-item"><span className="req-dot" />{r}</li>
                ))}
              </ul>
            </div>
          </div>

          {/* Screenshots (optional) */}
          {!alreadySubmitted && (
            <div className="section-block">
              <div className="section-label" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <span>Screenshots <span style={{ color: 'var(--muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{needsScreenshot ? '(required)' : '(optional)'}</span></span>
                </div>
                <button className="ss-add-btn" onClick={() => fileInputRef.current?.click()}>+ Add</button>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleScreenshots} />
              {screenshots.length > 0 ? (
                <div className="ss-strip">
                  {screenshots.map((img, i) => (
                    <div key={i} className="ss-thumb-wrap">
                      <img src={img.url} alt={img.name} className="ss-thumb" onClick={() => setLightbox(img.url)} />
                      {img.uploading && <div className="ss-uploading">...</div>}
                      {!img.uploading && <button className="ss-remove" onClick={() => removeScreenshot(i)}>✕</button>}
                    </div>
                  ))}
                  {uploading && <div className="ss-thumb-wrap" style={{ display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',fontSize:11 }}>Uploading…</div>}
                </div>
              ) : (
                <button className="ss-empty" onClick={() => fileInputRef.current?.click()}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <span>Click to add screenshots of your work</span>
                </button>
              )}

              {/* Screenshot-only tasks (e.g. cloud console work) mark complete here — no repo involved */}
              {submissionType === 'screenshot' && (
                !isCompleted ? (
                  <button className="complete-btn" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }} onClick={onMarkComplete} disabled={!canComplete}>
                    <CheckIcon /> Mark Complete
                  </button>
                ) : (
                  <button className="edit-again-btn" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }} onClick={onEditAgain}>✎ Edit</button>
                )
              )}
            </div>
          )}

          {/* Repository / Design Link */}
          {!alreadySubmitted && needsGithub && (
            <div className="section-block">
              <div className="section-label" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {linkType === 'figma' ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 12a4 4 0 0 1 4-4h0a4 4 0 0 1 0 8h0a4 4 0 0 1-4-4z"/><path d="M12 4h4a4 4 0 0 1 0 8h-4z"/><path d="M12 12h4a4 4 0 0 1 0 8 4 4 0 0 1-4-4z"/><path d="M8 4h4v8H8a4 4 0 0 1 0-8z"/></svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>
                  )}
                  <span>{linkType === 'figma' ? 'Your Figma Prototype Link' : 'Your GitHub Repository Link'}</span>
                </div>
                {linkType === 'github' && <button className="git-help-btn" onClick={onShowGitPush}>How to push?</button>}
              </div>
              <div className="gh-row">
                <input
                  type="url"
                  placeholder={linkType === 'figma' ? 'https://figma.com/file/...' : 'https://github.com/yourusername/repo-name'}
                  value={githubLink}
                  onChange={e => onLinkChange(e.target.value)}
                  className="gh-input"
                  disabled={isCompleted}
                />
                {!isCompleted ? (
                  <button
                    className="complete-btn"
                    onClick={onMarkComplete}
                    disabled={!canComplete}
                  >
                    <CheckIcon /> Mark Complete
                  </button>
                ) : (
                  <>
                    <a href={githubLink} target="_blank" rel="noreferrer" className="view-repo-btn">
                      {linkType === 'figma' ? 'View Design' : 'View Repo'} <ExternalIcon />
                    </a>
                    <button className="edit-again-btn" onClick={onEditAgain}>✎ Edit</button>
                  </>
                )}
              </div>
              {!isCompleted && (
                <p className="gh-hint">
                  {linkType === 'figma'
                    ? 'Paste a shareable Figma link (set to "Anyone with the link can view") to mark this task as done.'
                    : needsScreenshot
                      ? 'Paste your GitHub repo link AND add at least one screenshot above to mark this task as done.'
                      : 'Paste your public GitHub repository URL to mark this task as done.'}
                </p>
              )}
            </div>
          )}

          {/* Lightbox */}
          {lightbox && (
            <div className="ss-lightbox" onClick={() => setLightbox(null)}>
              <img src={lightbox} alt="Screenshot" className="ss-lightbox-img" onClick={e => e.stopPropagation()} />
              <button className="ss-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
            </div>
          )}

          {/* Already submitted state */}
          {alreadySubmitted && (
            <div className="section-block">
              <div className="submitted-info">
                <span style={{ fontSize: 22 }}>{status === 'completed' ? '🏆' : '✅'}</span>
                <div>
                  <p style={{ fontWeight: 700, marginBottom: 4 }}>
                    {status === 'completed' ? 'Approved by admin!' : 'Submitted — Under Review'}
                  </p>
                  {githubLink && (
                    <a href={githubLink} target="_blank" rel="noreferrer" className="repo-link">{githubLink}</a>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   STATUS SLIDE PANEL
───────────────────────────────────────────────────────────────── */
const StatusPanel = ({ status, onClose }) => {
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.in_progress;
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel-drawer" onClick={e => e.stopPropagation()}>
        <button className="panel-close" onClick={onClose}>✕</button>
        <div className="panel-emoji">{sc.emoji}</div>
        <div className="panel-status-pill" style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>{sc.label}</div>
        <h3 className="panel-title">Submitted Successfully!</h3>
        <p className="panel-motivation">{sc.motivation}</p>
        <p className="panel-sub">{sc.sub}</p>
        <div className="panel-timeline">
          {['Payment Verified', 'Tasks In Progress', 'Submitted for Review', 'Certificate Issued'].map((step, i) => {
            const reached = (i === 0 && ['in_progress','under_review','changes_requested','completed'].includes(status)) ||
              (i === 1 && ['in_progress','under_review','changes_requested','completed'].includes(status)) ||
              (i === 2 && ['under_review','completed'].includes(status)) ||
              (i === 3 && status === 'completed');
            return (
              <div key={i} className="timeline-row">
                <div className={`timeline-dot ${reached ? 'dot-active' : ''}`} />
                <span className={`timeline-label ${reached ? 'label-active' : ''}`}>{step}</span>
              </div>
            );
          })}
        </div>
        <button className="panel-dismiss" onClick={onClose}>Got it!</button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SIDEBAR STATUS WIDGET
───────────────────────────────────────────────────────────────── */
const StatusSidebar = ({ status, courseName, name, id, completedCount, totalTopics }) => {
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.pending_payment;
  return (
    <aside className="status-sidebar">
      <div className="sb-card">
        <p className="sb-label">Current Status</p>
        <div className="sb-status-pill" style={{ background: sc.bg, color: sc.color, borderColor: sc.border }}>
          <span className="sb-dot" style={{ background: sc.color }} />{sc.label}
        </div>
        <p className="sb-motivation">{sc.motivation}</p>
      </div>

      <div className="sb-card">
        <p className="sb-label">Your Progress</p>
        <div className="progress-steps">
          {['Payment Verified', 'Tasks In Progress', 'Submitted for Review', 'Certificate Issued'].map((step, i) => {
            const reached = (i === 0 && ['in_progress','under_review','changes_requested','completed'].includes(status)) ||
              (i === 1 && ['in_progress','under_review','changes_requested','completed'].includes(status)) ||
              (i === 2 && ['under_review','completed'].includes(status)) ||
              (i === 3 && status === 'completed');
            return (
              <div key={i} className="progress-row">
                <div className={`progress-dot ${reached ? 'prog-active' : ''}`} />
                {i < 3 && <div className={`progress-line ${reached && i < 2 ? 'line-active' : ''}`} />}
                <span className={`progress-label ${reached ? 'plabel-active' : ''}`}>{step}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sb-card sb-info">
        <p className="sb-label">Enrollment Details</p>
        <div className="info-row"><span className="info-key">Student</span><span className="info-val">{name}</span></div>
        <div className="info-row"><span className="info-key">Course</span><span className="info-val">{courseName}</span></div>
        <div className="info-row"><span className="info-key">ID</span><span className="info-val" style={{ fontFamily: 'monospace', fontSize: 11 }}>{id}</span></div>
        <div className="info-row"><span className="info-key">Tasks Done</span><span className="info-val">{completedCount} / {totalTopics}</span></div>
      </div>
    </aside>
  );
};

/* ─────────────────────────────────────────────────────────────────
   GIT PUSH MODAL
───────────────────────────────────────────────────────────────── */
const GitPushModal = ({ onClose }) => (
  <div className="rules-overlay" onClick={onClose}>
    <div className="rules-modal" onClick={e => e.stopPropagation()}>
      <div className="rules-modal-header">
        <span className="rules-modal-icon">🚀</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="rules-modal-title">How to Push Code to GitHub</h2>
          <span className="rules-modal-course">Beginner's Guide</span>
        </div>
        <button className="rules-modal-close" onClick={onClose}>✕</button>
      </div>

      <div className="rules-modal-body">
        <p className="rules-desc">Never used Git before? No worries — follow these steps exactly and you'll have your code on GitHub in minutes.</p>

        <div className="rules-divider" />

        <div className="rules-section-label"><span>📦</span><span>Step-by-Step Guide</span></div>
        <div className="rules-steps">
          <div className="rules-step"><span className="rules-step-dot">1</span>
            <span>Download and install Git from <a href="https://git-scm.com/downloads" target="_blank" rel="noreferrer" className="gp-link">git-scm.com/downloads</a>. Run the installer with default settings.</span>
          </div>
          <div className="rules-step"><span className="rules-step-dot">2</span>
            <span>Create a free account at <a href="https://github.com" target="_blank" rel="noreferrer" className="gp-link">github.com</a> if you don't have one.</span>
          </div>
          <div className="rules-step"><span className="rules-step-dot">3</span>
            <span>On GitHub, click <strong>New repository</strong>, give it a name, set it to <strong>Public</strong>, and click <strong>Create repository</strong>.</span>
          </div>
          <div className="rules-step"><span className="rules-step-dot">4</span>
            <span>Open a terminal (Command Prompt / Git Bash) inside your project folder.</span>
          </div>
          <div className="rules-step"><span className="rules-step-dot">5</span>
            <span>Run these commands one by one:</span>
          </div>
        </div>

        <div className="gp-code-block">
          <code>git init</code>
          <code>git add .</code>
          <code>git commit -m "first commit"</code>
          <code>git branch -M main</code>
          <code>git remote add origin https://github.com/YOUR_USERNAME/YOUR_REPO.git</code>
          <code>git push -u origin main</code>
        </div>

        <div className="rules-step" style={{ marginTop: 4 }}><span className="rules-step-dot">6</span>
          <span>Copy your repository URL from GitHub (it looks like <span className="gp-mono">https://github.com/yourname/repo</span>) and paste it in the task input field.</span>
        </div>

        <div className="rules-divider" />

        <div className="rules-section-label"><span>🎬</span><span>Learn by Watching</span></div>
        <div className="gp-links-grid">
          <a href="https://www.youtube.com/watch?v=RGOj5yH7evk" target="_blank" rel="noreferrer" className="gp-resource-card">
            <span className="gp-resource-icon">▶</span>
            <div>
              <p className="gp-resource-title">Git & GitHub Crash Course</p>
              <p className="gp-resource-sub">freeCodeCamp · YouTube · 1 hr</p>
            </div>
          </a>
          <a href="https://docs.github.com/en/get-started/quickstart/hello-world" target="_blank" rel="noreferrer" className="gp-resource-card">
            <span className="gp-resource-icon">📖</span>
            <div>
              <p className="gp-resource-title">GitHub Hello World Guide</p>
              <p className="gp-resource-sub">Official GitHub Docs</p>
            </div>
          </a>
        </div>

        <div className="rules-tip">
          <span style={{ fontSize: 20, flexShrink: 0 }}>💡</span>
          <p>After your first push, for future updates just run: <span className="gp-mono">git add . → git commit -m "update" → git push</span></p>
        </div>
      </div>

      <div className="rules-modal-footer">
        <button className="rules-got-it" onClick={onClose}>Got it — I'm ready to push! 💪</button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MOTIVATION MODAL
───────────────────────────────────────────────────────────────── */
const MotivationModal = ({ topic, onClose }) => (
  <div className="motiv-overlay" onClick={onClose}>
    <div className="motiv-modal" onClick={e => e.stopPropagation()}>
      <button className="rules-modal-close" style={{ position: 'absolute', top: 16, right: 16 }} onClick={onClose}>✕</button>
      <img src="/motivation_meme.png" alt="You did it!" className="motiv-meme" />
      <div className="motiv-content">
        <h2 className="motiv-title">Task Completed!</h2>
        <p className="motiv-msg">{topic?.motivation || "Amazing work! Keep going, you're on fire!"}</p>
        <button className="motiv-btn" onClick={onClose}>Keep Going</button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   CERTIFICATE SECTION
───────────────────────────────────────────────────────────────── */
const CertificateSection = ({ status, certificateUrl, certificateFormat, internshipId }) => {
  const isUnlocked = status === 'completed';
  const handleDownload = async () => {
    try {
      await downloadFile(fileUrl(certificateUrl), `ThiranX_Certificate_${internshipId}.${certificateFormat || 'pdf'}`);
    } catch {
      window.open(fileUrl(certificateUrl), '_blank');
    }
  };
  return (
    <div className="cert-section">
      <div className={`cert-box ${isUnlocked ? 'cert-unlocked' : 'cert-locked'}`}>
        <div className="cert-icon-wrap" style={{ color: isUnlocked ? '#10B981' : 'var(--muted)' }}>
          {isUnlocked ? (
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="6"></circle><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
            </svg>
          ) : <LockIcon />}
        </div>
        <div className="cert-content">
          <h3 className="cert-title" style={{ color: isUnlocked ? '#10B981' : 'var(--text)' }}>
            {isUnlocked ? 'Certificate Unlocked 🎉' : 'Certificate Locked 🔒'}
          </h3>
          <p className="cert-desc">
            {isUnlocked
              ? 'Congratulations! Your work was reviewed and approved. Download your verified certificate below.'
              : 'Complete all tasks, submit your work, and once our team approves your code — your certificate will appear here.'}
          </p>
          {isUnlocked && certificateUrl ? (
            <button onClick={handleDownload} className="cert-dl-btn">Download Certificate ↓</button>
          ) : !isUnlocked && (
            <div className="cert-steps">
              <span className="cert-step">① Complete all topics</span>
              <span className="cert-step">② Submit your project</span>
              <span className="cert-step">③ Get approved by our team</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   RULES — per-course descriptions and rules shown in the modal
───────────────────────────────────────────────────────────────── */
const COURSE_RULES = {
  "Frontend Web Development": {
    description: "Welcome to the Frontend Web Development MicroInternship! Over 4 structured modules you'll build real projects using HTML, CSS, and JavaScript — progressing from fundamentals to a complete final project.",
    rules: [
      "Complete tasks in order — each module builds on the previous one.",
      "All submitted GitHub repositories must be public.",
      "Code must be your own — plagiarism results in immediate disqualification.",
      "Each task requires a separate GitHub repository link.",
      "Your final project must include a README.md with setup instructions.",
      "Submissions are reviewed within 24–48 hours after the final project is submitted.",
      "You may re-submit if your work is rejected — review the feedback and improve.",
      "Reach out on WhatsApp/email if you're stuck — don't suffer in silence!",
    ],
    tips: "Start early, commit often, and write clean code. Recruiters look at your GitHub history!",
  },
  "Backend Node.js Development": {
    description: "Welcome to the Backend Node.js Development MicroInternship! You'll build real REST APIs, integrate MongoDB, and implement JWT authentication — skills that power every modern web application.",
    rules: [
      "Complete tasks in order — each builds directly on the last.",
      "All GitHub repositories must be public and include a README.md.",
      "No plagiarism — your code must be original work.",
      "Test all API endpoints with Postman or Thunder Client before submitting.",
      "Never commit sensitive credentials (API keys, DB URIs) to GitHub.",
      "Submissions are reviewed within 24–48 hours.",
      "Reach out via WhatsApp/email if you get stuck.",
    ],
    tips: "Use environment variables, write clean error handling, and comment your logic — these signal professional-level thinking.",
  },
  "MERN Stack Development": {
    description: "Welcome to the MERN Stack Development MicroInternship! You'll build an end-to-end blog platform — connecting a React frontend to an Express + MongoDB backend, with real authentication.",
    rules: [
      "Complete tasks in order — each builds directly on the last.",
      "Client and server must be in separate folders within one repository.",
      "All GitHub repositories must be public.",
      "No plagiarism — original work only.",
      "Include a README.md with clear setup instructions for both client and server.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Keep your frontend and backend cleanly decoupled. Use .env files and never hardcode secrets.",
  },
  "Python Programming": {
    description: "Welcome to the Python Programming MicroInternship! Over 4 structured modules you'll go from core syntax to a complete command-line application — the same progression every Python developer follows.",
    rules: [
      "Complete tasks in order — each module builds on the previous one.",
      "All submitted GitHub repositories must be public.",
      "Code must be your own — plagiarism results in immediate disqualification.",
      "Each task requires a separate GitHub repository link.",
      "Your final project must include a README.md with setup instructions.",
      "Submissions are reviewed within 24–48 hours after the final project is submitted.",
    ],
    tips: "Write clean, commented code and test edge cases (empty input, invalid values) — that's what separates working code from professional code.",
  },
  "Java Full Stack Development": {
    description: "Welcome to the Java Full Stack Development MicroInternship! You'll build REST APIs with Spring Boot, connect them to a database, secure them with JWT, and integrate a frontend — the full enterprise stack.",
    rules: [
      "Complete tasks in order — each builds directly on the last.",
      "All GitHub repositories must be public and include a README.md.",
      "No plagiarism — your code must be original work.",
      "Test all API endpoints with Postman or Thunder Client before submitting.",
      "Never commit sensitive credentials (DB passwords, JWT secrets) to GitHub.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Structure your Spring Boot project cleanly (controller/service/repository layers) — recruiters notice well-organized Java code.",
  },
  "Cloud Development": {
    description: "Welcome to the Cloud Development MicroInternship! You'll get hands-on with AWS — launching servers, hosting storage, working with managed databases, and deploying real applications to the cloud.",
    rules: [
      "Complete tasks in order — each builds directly on the last.",
      "Some tasks require a GitHub link, some require screenshots, and some require both — check each task's requirements carefully.",
      "Never commit AWS keys or secrets to GitHub — use environment variables.",
      "Stay within AWS Free Tier limits to avoid unexpected charges.",
      "Screenshots must clearly show the relevant AWS console screen or terminal output.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Set a billing alert on day one. Cloud consoles change often — if a button has moved, search 'AWS Console — [feature name]' for the latest steps.",
  },
  "Mobile App Development": {
    description: "Welcome to the Mobile App Development MicroInternship! You'll build real cross-platform apps with React Native — from your first screen to a complete, native-feeling app.",
    rules: [
      "Complete tasks in order — each module builds on the previous one.",
      "All submitted GitHub repositories must be public.",
      "Code must be your own — plagiarism results in immediate disqualification.",
      "Each task requires a separate GitHub repository link.",
      "Test on an emulator or a real device via Expo Go before submitting.",
      "Your final project must include a README.md with setup instructions.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Test on both a small and large screen size early — layout bugs are far easier to fix before your app has 10 screens.",
  },
  "DevOps": {
    description: "Welcome to the DevOps MicroInternship! You'll go from Git workflows to a full CI/CD pipeline — automating build, test, and deployment the way real engineering teams do.",
    rules: [
      "Complete tasks in order — each builds directly on the last.",
      "Some tasks require a GitHub link, some require screenshots, and some require both — check each task's requirements carefully.",
      "Never commit secrets, tokens, or API keys to your repository — use environment variables or GitHub Actions secrets.",
      "Screenshots must clearly show the relevant pipeline run, terminal output, or dashboard.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Commit your GitHub Actions workflow file early and iterate on it — a broken pipeline is much easier to debug with small, incremental changes.",
  },
  "UI/UX Design": {
    description: "Welcome to the UI/UX Design MicroInternship! You'll learn design systems, Figma, and high-fidelity prototyping — the skills that make products beautiful and intuitive.",
    rules: [
      "All submissions must be shareable Figma links (not screenshots).",
      "The final prototype task requires a Loom screen recording of the flow.",
      "Designs must follow the style guide you create in Module 1.",
      "No copying other designers' work — original designs only.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Great design comes from iteration. Get feedback early, refine often, and always design for the user first.",
  },
  "Python Data Science": {
    description: "Welcome to the Python Data Science MicroInternship! You'll analyze real datasets, create visualizations, and extract meaningful insights — the core workflow of every data scientist.",
    rules: [
      "Submit Jupyter notebooks (.ipynb) via a public GitHub repository.",
      "Each notebook must include markdown cells explaining your findings.",
      "No plagiarism — analysis and insights must be your own.",
      "All charts must have titles, axis labels, and legends.",
      "Submissions are reviewed within 24–48 hours.",
    ],
    tips: "Clean data before analysing it. Add markdown explanations between code cells — notebooks that tell a story stand out.",
  },
};

/* ─────────────────────────────────────────────────────────────────
   RULES MODAL COMPONENT
───────────────────────────────────────────────────────────────── */
const RulesModal = ({ courseName, onClose }) => {
  const info = COURSE_RULES[courseName] || {
    description: "Complete the tasks below, submit your GitHub links, and our team will review your work.",
    rules: ["Complete all tasks in order.", "Submit public GitHub repository links.", "No plagiarism."],
    tips: "Work consistently and ask for help if you're stuck!",
  };

  return (
    <div className="rules-overlay" onClick={onClose}>
      <div className="rules-modal" onClick={e => e.stopPropagation()}>

        {/* Fixed Header */}
        <div className="rules-modal-header">
          <span className="rules-modal-icon">📋</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="rules-modal-title">Welcome to your MicroInternship</h2>
            <span className="rules-modal-course">{courseName}</span>
          </div>
          <button className="rules-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Single Scrollable Body */}
        <div className="rules-modal-body">

          {/* Description */}
          <p className="rules-desc">{info.description}</p>

          <div className="rules-divider" />

          {/* Rules */}
          <div className="rules-section-label"><span>🚦</span><span>Internship Rules</span></div>
          <ul className="rules-list">
            {info.rules.map((rule, i) => (
              <li key={i} className="rules-item">
                <span className="rules-num">{i + 1}</span>
                <span>{rule}</span>
              </li>
            ))}
          </ul>

          <div className="rules-divider" />

          {/* How to Submit — simple & clear */}
          <div className="rules-section-label"><span>📤</span><span>How to Submit</span></div>
          <div className="rules-steps">
            <div className="rules-step">
              <span className="rules-step-dot">1</span>
              <span>Complete a task, then open it and paste your <strong>public GitHub repository link</strong> in the input field.</span>
            </div>
            <div className="rules-step">
              <span className="rules-step-dot">2</span>
              <span>Click <strong>Mark Complete</strong> — do this for every task.</span>
            </div>
            <div className="rules-step">
              <span className="rules-step-dot">3</span>
              <span>Once all tasks are done, click <strong>Submit Final Project for Review</strong> at the bottom.</span>
            </div>
          </div>

          {/* Tip */}
          <div className="rules-tip">
            <span style={{ fontSize: 20, flexShrink: 0 }}>💡</span>
            <p>{info.tips}</p>
          </div>

        </div>

        {/* Fixed Footer */}
        <div className="rules-modal-footer">
          <button className="rules-got-it" onClick={onClose}>Got it — Let's get started! 🚀</button>
        </div>

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */
const MicrointernshipTask = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');

  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Per-task local state (persisted in localStorage)
  const [completedTopics, setCompletedTopics] = useState([]);
  const [topicLinks, setTopicLinks]           = useState([]);

  // Final submit
  const [submitting, setSubmitting]   = useState(false);
  const [showPanel, setShowPanel]     = useState(false);

  // Modals
  const [showRules, setShowRules]         = useState(false);
  const [showGitPush, setShowGitPush]     = useState(false);
  const [showMotivation, setShowMotivation] = useState(null); // stores the completed topic object

  useEffect(() => {
    if (!email) { setError("Email parameter missing. Invalid link."); setLoading(false); return; }
    API.get(`/microinternships/my-task/${id}?email=${email}`)
      .then(res => {
        setTaskData(res.data);
        const topics = COURSE_TASKS[res.data.courseName] || [];
        // Load persisted per-task state
        const savedCompleted = JSON.parse(localStorage.getItem(`micro_done_${id}`) || 'null');
        const savedLinks     = JSON.parse(localStorage.getItem(`micro_links_${id}`) || 'null');
        setCompletedTopics(savedCompleted || Array(topics.length).fill(false));
        setTopicLinks(savedLinks || Array(topics.length).fill(''));
        // Load initial screenshots from localStorage (populated above from server)
        const savedScreens = JSON.parse(localStorage.getItem(`micro_ss_${id}`) || 'null');
        if (savedScreens && Array.isArray(savedScreens)) {
          // Attach to window so TopicCard's initialScreenshots prop can read it synchronously
          window.__micro_ss__ = window.__micro_ss__ || {};
          window.__micro_ss__[id] = savedScreens;
        }
        // Initialize per-topic screenshots from server data
        if (res.data.taskSubmissions && Array.isArray(res.data.taskSubmissions)) {
          // taskSubmissions may not be in order; create an array of screenshots per global index
          const initialScreens = Array(topics.length).fill(null).map(() => []);
          res.data.taskSubmissions.forEach(ts => {
            if (typeof ts.taskIndex === 'number' && ts.screenshots && ts.screenshots.length) {
              initialScreens[ts.taskIndex] = ts.screenshots;
            }
          });
          // store in state so TopicCard can pick it via props later using localStorage fallback
          localStorage.setItem(`micro_ss_${id}`, JSON.stringify(initialScreens));
        }
        // Show rules modal on first visit (only when tasks are unlocked)
        const hasSeen = localStorage.getItem(`micro_rules_seen_${id}`);
        if (!hasSeen && res.data.status !== 'pending_payment') {
          setShowRules(true);
          localStorage.setItem(`micro_rules_seen_${id}`, '1');
        }
      })
      .catch(() => setError("Could not find your enrollment. Please check the link."))
      .finally(() => setLoading(false));
  }, [id, email]);

  const saveToLocal = useCallback((done, links) => {
    localStorage.setItem(`micro_done_${id}`, JSON.stringify(done));
    localStorage.setItem(`micro_links_${id}`, JSON.stringify(links));
  }, [id]);

  const handleLinkChange = (i, val) => {
    const next = [...topicLinks]; next[i] = val;
    setTopicLinks(next); saveToLocal(completedTopics, next);
  };

  const handleMarkComplete = (i) => {
    const next = [...completedTopics]; next[i] = true;
    setCompletedTopics(next); saveToLocal(next, topicLinks);
    // Show motivation popup
    const topics = COURSE_TASKS[taskData?.courseName] || [];
    setShowMotivation(topics[i] || null);
  };

  const handleEditAgain = (i) => {
    const next = [...completedTopics]; next[i] = false;
    setCompletedTopics(next); saveToLocal(next, topicLinks);
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const topicsArr = COURSE_TASKS[taskData?.courseName] || [];
      const taskLinks = topicLinks
        .map((link, i) => ({
          taskIndex: i,
          topicName: topicsArr[i]?.topic || `Task ${i + 1}`,
          githubLink: link || '',
        }))
        .filter(t => t.githubLink);

      const res = await API.put(`/microinternships/submit-task/${id}`, {
        email,
        githubRepo: topicLinks.filter(Boolean).join(', ') || 'submitted-via-dashboard',
        taskLinks,
      });
      setTaskData(prev => ({ ...prev, status: res.data.status }));
      setShowPanel(true);
    } catch { alert('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loader-ring" />
    </div>
  );
  if (error) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 40 }}>
      <span style={{ fontSize: 48 }}>⚠️</span>
      <p style={{ color: 'var(--danger)', fontWeight: 700, fontSize: 18, textAlign: 'center' }}>{error}</p>
    </div>
  );

  const { status, courseName, name, certificateUrl, certificateFormat, reviewNote } = taskData;
  const topics = COURSE_TASKS[courseName] || [];
  const completedCount = completedTopics.filter(Boolean).length;
  const allDone = completedCount === topics.length && topics.length > 0;
  const alreadySubmitted = status === 'under_review' || status === 'completed';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

        .task-page { min-height: 100vh; background: var(--bg); color: var(--text); font-family: 'Space Grotesk', system-ui, sans-serif; padding-bottom: 80px; }

        .task-page-header { padding: 80px 20px 36px; max-width: 1160px; margin: 0 auto; }
        .header-meta { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
        .meta-chip { padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 600; border: 1px solid var(--border); color: var(--muted); background: var(--bg2); letter-spacing: 0.04em; text-transform: uppercase; }
        .page-h1 { font-size: clamp(24px, 3.5vw, 36px); font-weight: 700; letter-spacing: -0.02em; margin-bottom: 6px; }
        .page-sub { color: var(--muted); font-size: 15px; }
        .page-sub strong { color: var(--text); }

        /* ── Progress Bar ── */
        .changes-banner { display: flex; align-items: flex-start; gap: 12px; background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.3); border-radius: 14px; padding: 16px 18px; margin-bottom: 16px; }
        .changes-banner-emoji { font-size: 22px; flex-shrink: 0; }
        .changes-banner-title { font-size: 14px; font-weight: 700; color: #F87171; margin: 0 0 4px; }
        .changes-banner-note { font-size: 13px; color: var(--text-2); line-height: 1.6; margin: 0; }
        .progress-bar-wrap { background: var(--bg2); border: 1px solid var(--border); border-radius: 14px; padding: 18px 20px; margin-bottom: 12px; }
        .progress-bar-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .progress-bar-label { font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--muted); }
        .progress-bar-count { font-size: 13px; font-weight: 600; color: var(--text); }
        .progress-bar-track { height: 8px; background: var(--bg3); border-radius: 999px; overflow: hidden; }
        .progress-bar-fill { height: 100%; background: var(--v1); border-radius: 999px; transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .progress-bar-pct { font-size: 11px; color: var(--muted); text-align: right; margin-top: 6px; }

        /* ── Layout ── */
        .task-layout { max-width: 1160px; margin: 0 auto; padding: 0 20px; display: grid; grid-template-columns: 1fr 300px; gap: 24px; align-items: start; }
        @media (max-width: 900px) { .task-layout { grid-template-columns: 1fr; } .status-sidebar { order: -1; } }
        .topics-col {}
        .topics-heading-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
        .topics-heading { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }
        .view-rules-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; background: rgba(127,0,255,0.08); color: var(--v2); border: 1px solid rgba(127,0,255,0.25); border-radius: 8px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; letter-spacing: 0.03em; transition: background 0.2s, border-color 0.2s; white-space: nowrap; }
        .view-rules-btn:hover { background: rgba(127,0,255,0.15); border-color: rgba(127,0,255,0.4); }

        /* ── Git Push CTA — big banner shown before the task list ── */
        .git-push-cta { width: 100%; display: flex; align-items: center; gap: 16px; padding: 18px 22px; margin-bottom: 16px; background: linear-gradient(135deg, rgba(127,0,255,0.12), rgba(177,99,255,0.08)); border: 1.5px solid rgba(127,0,255,0.3); border-radius: 16px; cursor: pointer; text-align: left; font-family: 'Space Grotesk', sans-serif; transition: border-color 0.2s, transform 0.2s; }
        .git-push-cta:hover { border-color: rgba(127,0,255,0.55); transform: translateY(-1px); }
        .git-push-cta-icon { font-size: 30px; flex-shrink: 0; }
        .git-push-cta-text { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
        .git-push-cta-title { font-size: 16px; font-weight: 800; color: var(--text); }
        .git-push-cta-sub { font-size: 13px; color: var(--muted); }
        .git-push-cta-arrow { font-size: 20px; color: var(--v1); flex-shrink: 0; }
        @media (max-width: 500px) {
          .git-push-cta { padding: 14px 16px; gap: 12px; }
          .git-push-cta-icon { font-size: 24px; }
          .git-push-cta-title { font-size: 14px; }
          .git-push-cta-sub { font-size: 12px; }
        }

        /* ── Git help btn inside card ── */
        .git-help-btn { font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 7px; background: rgba(127,0,255,0.07); color: var(--v2); border: 1px solid rgba(127,0,255,0.2); cursor: pointer; font-family: 'Space Grotesk', sans-serif; white-space: nowrap; transition: background 0.15s; }
        .git-help-btn:hover { background: rgba(127,0,255,0.15); }

        /* ── Screenshots ── */
        .ss-add-btn { font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 7px; background: var(--bg3); color: var(--muted); border: 1px solid var(--border); cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: background 0.15s; }
        .ss-add-btn:hover { background: var(--bg4); color: var(--text); }
        .ss-strip { display: flex; flex-wrap: wrap; gap: 8px; }
        .ss-thumb-wrap { position: relative; width: 80px; height: 60px; border-radius: 8px; overflow: hidden; border: 1px solid var(--border); flex-shrink: 0; cursor: pointer; }
        .ss-thumb { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.2s; }
        .ss-thumb:hover { transform: scale(1.06); }
        .ss-remove { position: absolute; top: 3px; right: 3px; background: rgba(0,0,0,0.6); color: #fff; border: none; border-radius: 50%; width: 16px; height: 16px; font-size: 9px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .ss-empty { display: flex; align-items: center; gap: 8px; width: 100%; padding: 12px 16px; background: var(--bg3); border: 1.5px dashed var(--border); border-radius: 10px; color: var(--muted); font-size: 12px; font-weight: 500; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: border-color 0.2s, background 0.2s; }
        .ss-empty:hover { border-color: var(--v1); background: rgba(127,0,255,0.04); color: var(--text); }
        .ss-lightbox { position: fixed; inset: 0; z-index: 9000; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; cursor: pointer; }
        .ss-lightbox-img { max-width: min(90vw, 1000px); max-height: 85vh; border-radius: 12px; object-fit: contain; cursor: default; box-shadow: 0 24px 80px rgba(0,0,0,0.6); }
        .ss-lightbox-close { position: absolute; top: 20px; right: 20px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: #fff; width: 40px; height: 40px; border-radius: 50%; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .ss-uploading { position: absolute; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 9px; font-weight: 700; border-radius: 8px; }

        /* ── Git Push Modal extras ── */
        .gp-code-block { background: #0d1117; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 16px 20px; display: flex; flex-direction: column; gap: 6px; margin: 4px 0; }
        .gp-code-block code { font-family: 'Courier New', monospace; font-size: 13px; color: #7ee787; }
        .gp-link { color: var(--v2); text-decoration: underline; text-underline-offset: 2px; }
        .gp-mono { font-family: monospace; font-size: 12px; background: var(--bg3); padding: 2px 6px; border-radius: 4px; color: var(--v2); }
        .gp-links-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 500px) { .gp-links-grid { grid-template-columns: 1fr; } }
        .gp-resource-card { display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: var(--bg3); border: 1px solid var(--border); border-radius: 12px; text-decoration: none; color: var(--text); transition: border-color 0.2s, background 0.2s; }
        .gp-resource-card:hover { border-color: var(--v1); background: rgba(127,0,255,0.05); }
        .gp-resource-icon { width: 36px; height: 36px; border-radius: 10px; background: var(--v1); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; flex-shrink: 0; }
        .gp-resource-title { font-size: 13px; font-weight: 700; margin-bottom: 3px; }
        .gp-resource-sub { font-size: 11px; color: var(--muted); }

        /* ── Motivation Modal ── */
        .motiv-overlay { position: fixed; inset: 0; z-index: 8000; background: rgba(0,0,0,0.7); backdrop-filter: blur(12px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
        .motiv-modal { width: min(460px, 100%); background: var(--bg2); border: 1px solid var(--border); border-radius: 24px; overflow: hidden; animation: scaleIn 0.35s cubic-bezier(0.16,1,0.3,1); position: relative; text-align: center; }
        .motiv-meme { width: 100%; height: 260px; object-fit: contain; object-position: center; display: block; background: var(--bg3); }
        .motiv-content { padding: 20px 28px 28px; }
        .motiv-title { font-size: 22px; font-weight: 800; margin-bottom: 10px; background: linear-gradient(135deg, var(--v1), #a855f7); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .motiv-msg { font-size: 15px; color: var(--text-2); line-height: 1.7; margin-bottom: 20px; }
        .motiv-btn { padding: 13px 32px; background: linear-gradient(135deg, var(--v1), #a855f7); color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: opacity 0.2s; }
        .motiv-btn:hover { opacity: 0.88; }

        /* ── Rules Modal ── */
        .rules-overlay { position: fixed; inset: 0; z-index: 7000; background: rgba(0,0,0,0.65); backdrop-filter: blur(10px); display: flex; align-items: center; justify-content: center; padding: 16px; animation: fadeIn 0.2s ease; }
        .rules-modal { width: min(660px, 100%); max-height: 90vh; background: var(--bg2); border: 1px solid var(--border); border-radius: 22px; display: flex; flex-direction: column; overflow: hidden; animation: scaleIn 0.3s cubic-bezier(0.16,1,0.3,1); transform: translateZ(0); will-change: transform; isolation: isolate; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.94) translateY(16px); } to { opacity: 1; transform: scale(1) translateY(0); } }

        /* Fixed header */
        .rules-modal-header { display: flex; align-items: center; gap: 16px; padding: 26px 28px 22px; border-bottom: 1px solid var(--border); flex-shrink: 0; }
        .rules-modal-icon { font-size: 34px; flex-shrink: 0; line-height: 1; }
        .rules-modal-title { font-size: 20px; font-weight: 700; margin-bottom: 6px; line-height: 1.2; }
        .rules-modal-course { font-size: 13px; color: var(--v2); font-weight: 600; background: rgba(127,0,255,0.09); padding: 3px 12px; border-radius: 999px; display: inline-block; border: 1px solid rgba(127,0,255,0.22); }
        .rules-modal-close { margin-left: auto; flex-shrink: 0; background: var(--bg3); border: 1px solid var(--border); width: 36px; height: 36px; border-radius: 50%; color: var(--muted); font-size: 15px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
        .rules-modal-close:hover { background: var(--bg4); color: var(--text); }

        /* Single scrollable body — NO inner overflow */
        .rules-modal-body { flex: 1; overflow-y: auto; padding: 26px 28px; display: flex; flex-direction: column; gap: 22px; scroll-behavior: smooth; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; will-change: scroll-position; }
        .rules-modal-body::-webkit-scrollbar { width: 5px; }
        .rules-modal-body::-webkit-scrollbar-track { background: transparent; }
        .rules-modal-body::-webkit-scrollbar-thumb { background: var(--border); border-radius: 999px; }

        .rules-desc { font-size: 15px; color: var(--text-2); line-height: 1.8; margin: 0; }
        .rules-divider { height: 1px; background: var(--border); flex-shrink: 0; }
        .rules-section-label { display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: var(--v2); margin-bottom: 14px; }
        .rules-list { list-style: none; display: flex; flex-direction: column; gap: 12px; margin: 0; padding: 0; }
        .rules-item { display: flex; align-items: flex-start; gap: 12px; font-size: 15px; color: var(--text-2); line-height: 1.6; }
        .rules-num { min-width: 24px; height: 24px; background: rgba(127,0,255,0.1); border: 1px solid rgba(127,0,255,0.2); color: var(--v2); border-radius: 7px; font-size: 11px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
        .rules-steps { display: flex; flex-direction: column; gap: 14px; }
        .rules-step { display: flex; align-items: flex-start; gap: 14px; font-size: 15px; color: var(--text-2); line-height: 1.6; }
        .rules-step-dot { width: 26px; height: 26px; border-radius: 50%; background: var(--v1); color: #fff; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
        .rules-tip { padding: 16px 18px; background: rgba(245,158,11,0.07); border: 1px solid rgba(245,158,11,0.22); border-radius: 14px; display: flex; align-items: flex-start; gap: 12px; }
        .rules-tip p { font-size: 14px; color: var(--text-2); line-height: 1.7; font-style: italic; margin: 0; }

        /* Fixed footer */
        .rules-modal-footer { padding: 18px 28px; border-top: 1px solid var(--border); flex-shrink: 0; }
        .rules-got-it { width: 100%; padding: 15px; background: var(--v1); color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: opacity 0.2s; }
        .rules-got-it:hover { opacity: 0.87; }

        @media (max-width: 500px) {
          .rules-modal-header { padding: 20px 18px 16px; gap: 12px; }
          .rules-modal-body { padding: 20px 18px; gap: 18px; }
          .rules-modal-footer { padding: 14px 18px; }
          .rules-modal-title { font-size: 17px; }
          .rules-desc, .rules-item, .rules-step { font-size: 14px; }
        }

        /* ── Module Header ── */
        .module-header { display: flex; align-items: center; gap: 10px; padding: 10px 0 8px; margin-top: 8px; border-bottom: 1px solid var(--border); margin-bottom: 10px; }
        .module-emoji { font-size: 18px; }
        .module-title { font-size: 13px; font-weight: 700; color: var(--text); letter-spacing: -0.01em; }
        .module-pill { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; background: rgba(127,0,255,0.08); color: var(--v2); border: 1px solid rgba(127,0,255,0.2); margin-left: auto; }
        .module-group { display: flex; flex-direction: column; gap: 10px; margin-bottom: 4px; }

        /* ── Topic description ── */
        .topic-description { font-size: 13px; color: var(--muted); line-height: 1.6; margin-bottom: 10px; font-style: italic; }
        .topics-stack { display: flex; flex-direction: column; gap: 10px; }

        /* ── Topic Card ── */
        .topic-card { border: 1px solid var(--border); border-radius: 14px; overflow: hidden; background: var(--bg2); transition: border-color 0.2s; }
        .topic-card:hover { border-color: var(--border-strong); }
        .topic-card-done { border-color: rgba(16,185,129,0.35) !important; }

        .topic-header { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: transparent; border: none; cursor: pointer; color: var(--text); text-align: left; gap: 12px; transition: background 0.15s; }
        .topic-header:hover { background: var(--bg3); }
        .topic-header:disabled { cursor: not-allowed; }

        .topic-num { width: 28px; height: 28px; border-radius: 8px; background: var(--bg3); border: 1px solid var(--border); color: var(--muted); font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .topic-num-done { background: #10B981 !important; border-color: #10B981 !important; color: #fff !important; }
        .topic-ttl { font-size: 14px; font-weight: 600; display: block; }
        .topic-meta { font-size: 11px; color: var(--muted); margin-top: 2px; display: block; }
        .badge-done { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: rgba(16,185,129,0.1); color: #10B981; border: 1px solid rgba(16,185,129,0.3); }
        .badge-locked { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: var(--bg3); color: var(--muted); border: 1px solid var(--border); }

        /* Motivation Banner */
        .motivation-banner { display: flex; align-items: flex-start; gap: 10px; background: rgba(16,185,129,0.07); border-top: 1px solid rgba(16,185,129,0.25); padding: 12px 20px; }
        .motivation-banner p { font-size: 13px; color: #10B981; font-weight: 500; line-height: 1.5; }

        .topic-body { padding: 0 20px 20px; display: flex; flex-direction: column; gap: 18px; border-top: 1px solid var(--border); }
        .section-block { padding-top: 18px; }
        .section-label { display: flex; align-items: center; gap: 7px; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--v2); margin-bottom: 10px; }
        .resource-box { background: var(--bg3); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .resource-name { font-size: 13px; font-weight: 500; color: var(--text); }
        .open-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; background: var(--v1); color: #fff; border-radius: 7px; font-size: 12px; font-weight: 600; text-decoration: none; white-space: nowrap; transition: opacity 0.2s; flex-shrink: 0; }
        .open-btn:hover { opacity: 0.85; }
        .task-desc { font-size: 14px; line-height: 1.7; color: var(--text-2); margin-bottom: 12px; }
        .req-box { background: var(--bg3); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; }
        .req-head { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
        .req-list { list-style: none; display: flex; flex-direction: column; gap: 8px; }
        .req-item { display: flex; align-items: flex-start; gap: 9px; font-size: 13px; color: var(--text-2); line-height: 1.5; }
        .req-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--v1); flex-shrink: 0; margin-top: 5px; }

        /* GitHub + Complete row */
        .gh-row { display: flex; gap: 8px; flex-wrap: wrap; }
        .gh-input { flex: 1; min-width: 180px; padding: 11px 13px; background: var(--bg3); border: 1.5px solid var(--border); border-radius: 8px; color: var(--text); font-size: 13px; outline: none; font-family: 'Space Grotesk', sans-serif; transition: border-color 0.2s; }
        .gh-input:focus { border-color: var(--v1); }
        .gh-input:disabled { opacity: 0.6; }
        .gh-hint { font-size: 11px; color: var(--muted); margin-top: 7px; }
        .complete-btn { display: inline-flex; align-items: center; gap: 6px; padding: 11px 16px; background: #10B981; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; white-space: nowrap; transition: opacity 0.2s; }
        .complete-btn:hover { opacity: 0.85; }
        .complete-btn:disabled { background: var(--bg4); color: var(--muted); cursor: not-allowed; }
        .view-repo-btn { display: inline-flex; align-items: center; gap: 6px; padding: 11px 14px; background: var(--bg3); color: var(--v2); border: 1px solid var(--border); border-radius: 8px; font-size: 12px; font-weight: 600; text-decoration: none; white-space: nowrap; }
        .edit-again-btn { padding: 11px 12px; background: transparent; color: var(--muted); border: 1px solid var(--border); border-radius: 8px; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: color 0.15s, border-color 0.15s; }
        .edit-again-btn:hover { color: var(--text); border-color: var(--border-strong); }

        .submitted-info { display: flex; align-items: flex-start; gap: 12px; background: var(--bg3); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; }
        .repo-link { font-size: 12px; color: var(--v2); word-break: break-all; text-decoration: underline; }

        /* ── Final Submit Button ── */
        .final-submit-wrap { margin-top: 12px; }
        .final-submit-btn { width: 100%; padding: 15px 20px; background: var(--v1); color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: opacity 0.2s; display: flex; align-items: center; justify-content: center; gap: 8px; }
        .final-submit-btn:hover { opacity: 0.85; }
        .final-submit-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .final-submit-note { font-size: 12px; color: var(--muted); text-align: center; margin-top: 8px; }

        /* ── Status Slide Panel ── */
        .panel-overlay { position: fixed; inset: 0; z-index: 6000; background: rgba(0,0,0,0.5); backdrop-filter: blur(6px); display: flex; align-items: stretch; justify-content: flex-end; animation: fadeIn 0.25s ease; }
        .panel-drawer { width: min(380px, 92vw); background: var(--bg2); border-left: 1px solid var(--border); padding: 40px 28px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; position: relative; animation: slideInRight 0.35s cubic-bezier(0.16,1,0.3,1); }
        @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .panel-close { position: absolute; top: 16px; right: 16px; background: var(--bg3); border: none; width: 32px; height: 32px; border-radius: 50%; color: var(--text); font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .panel-close:hover { background: var(--bg4); }
        .panel-emoji { font-size: 44px; }
        .panel-status-pill { display: inline-flex; align-items: center; padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; border: 1px solid; width: fit-content; }
        .panel-title { font-size: 20px; font-weight: 700; }
        .panel-motivation { font-size: 14px; line-height: 1.7; color: var(--text-2); }
        .panel-sub { font-size: 12px; color: var(--muted); }
        .panel-timeline { display: flex; flex-direction: column; gap: 0; background: var(--bg3); border: 1px solid var(--border); border-radius: 12px; padding: 16px; }
        .timeline-row { display: flex; align-items: center; gap: 12px; padding: 8px 0; position: relative; }
        .timeline-row:not(:last-child)::after { content: ''; position: absolute; left: 8px; top: 28px; width: 2px; height: 16px; background: var(--border); }
        .timeline-dot { width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0; background: var(--bg4); border: 2px solid var(--border); }
        .dot-active { background: var(--v1); border-color: var(--v1); box-shadow: 0 0 0 3px rgba(127,0,255,0.2); }
        .timeline-label { font-size: 13px; color: var(--muted); }
        .label-active { color: var(--text); font-weight: 600; }
        .panel-dismiss { padding: 12px; background: var(--v1); color: #fff; border: none; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Space Grotesk', sans-serif; transition: opacity 0.2s; }
        .panel-dismiss:hover { opacity: 0.85; }

        /* ── Right Sidebar ── */
        .status-sidebar { display: flex; flex-direction: column; gap: 14px; position: sticky; top: 100px; }
        .sb-card { background: var(--bg2); border: 1px solid var(--border); border-radius: 14px; padding: 18px; }
        .sb-label { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); margin-bottom: 10px; }
        .sb-status-pill { display: flex; align-items: center; gap: 7px; padding: 7px 12px; border-radius: 9px; font-size: 12px; font-weight: 700; border: 1px solid; margin-bottom: 12px; }
        .sb-dot { width: 7px; height: 7px; border-radius: 50%; animation: pulse 1.8s ease-in-out infinite; }
        @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
        .sb-motivation { font-size: 12.5px; color: var(--text-2); line-height: 1.6; }
        .progress-steps { display: flex; flex-direction: column; gap: 0; }
        .progress-row { display: flex; align-items: flex-start; gap: 10px; position: relative; }
        .progress-dot { width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0; background: var(--bg4); border: 2px solid var(--border); margin-top: 3px; transition: all 0.3s; }
        .prog-active { background: var(--v1); border-color: var(--v1); box-shadow: 0 0 0 3px rgba(127,0,255,0.2); }
        .progress-line { position: absolute; left: 6px; top: 17px; width: 2px; height: 22px; background: var(--border); }
        .line-active { background: var(--v1); }
        .progress-label { font-size: 12px; color: var(--muted); padding: 2px 0 18px; line-height: 1.4; }
        .plabel-active { color: var(--text); font-weight: 600; }
        .sb-info { display: flex; flex-direction: column; gap: 10px; }
        .info-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .info-key { font-size: 11px; color: var(--muted); flex-shrink: 0; }
        .info-val { font-size: 12px; font-weight: 600; color: var(--text); text-align: right; word-break: break-word; }

        /* ── Certificate ── */
        .cert-section { max-width: 1160px; margin: 24px auto 0; padding: 0 20px; }
        .cert-box { border-radius: 16px; padding: 32px; display: flex; align-items: center; gap: 24px; flex-wrap: wrap; border: 1px solid; }
        .cert-locked { border-color: var(--border); background: var(--bg2); }
        .cert-unlocked { border-color: rgba(16,185,129,0.35); background: rgba(16,185,129,0.05); }
        .cert-icon-wrap { flex-shrink: 0; }
        .cert-content { flex: 1; min-width: 220px; }
        .cert-title { font-size: 18px; font-weight: 700; margin-bottom: 6px; }
        .cert-desc { font-size: 13px; color: var(--muted); line-height: 1.6; margin-bottom: 16px; }
        .cert-dl-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #10B981; color: #fff; border-radius: 9px; font-weight: 700; font-size: 14px; text-decoration: none; transition: opacity 0.2s; border: none; cursor: pointer; font-family: 'Space Grotesk', sans-serif; }
        .cert-dl-btn:hover { opacity: 0.85; }
        .cert-steps { display: flex; gap: 8px; flex-wrap: wrap; }
        .cert-step { padding: 6px 12px; background: var(--bg3); border: 1px solid var(--border); border-radius: 999px; font-size: 12px; color: var(--muted); font-weight: 500; }

        /* ── Pending Box ── */
        .pending-wrap { max-width: 1160px; margin: 0 auto 24px; padding: 0 20px; }
        .pending-box { border: 1px dashed var(--border-strong); border-radius: 16px; padding: 48px 32px; text-align: center; background: var(--bg2); }
        .pending-emoji { font-size: 48px; margin-bottom: 16px; }
        .pending-title { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
        .pending-desc { color: var(--muted); font-size: 14px; line-height: 1.7; max-width: 420px; margin: 0 auto; }

        /* ── Loader ── */
        .loader-ring { width: 44px; height: 44px; border: 3px solid var(--border); border-top-color: var(--v1); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 600px) {
          .cert-box { flex-direction: column; }
          .gh-row { flex-direction: column; }
          .complete-btn { width: 100%; justify-content: center; }
          .resource-box { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="task-page">

        {/* Header */}
        <div className="task-page-header">
          <div className="header-meta">
            <span className="meta-chip">{id}</span>
            <span className="meta-chip">{courseName}</span>
          </div>
          <h1 className="page-h1">Internship Dashboard</h1>
          <p className="page-sub">Welcome back, <strong>{name}</strong> — here are your tasks and resources.</p>
        </div>

        {/* Pending */}
        {status === 'pending_payment' && (
          <div className="pending-wrap">
            <div className="pending-box">
              <div className="pending-emoji">⏳</div>
              <p className="pending-title">Payment Verification in Progress</p>
              <p className="pending-desc">We received your screenshot. Our team will verify your payment and unlock all tasks within <strong>1–2 hours</strong>. Save this page URL and come back soon!</p>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        {(status === 'in_progress' || status === 'under_review' || status === 'changes_requested' || status === 'completed') && (
          <div className="task-layout">
            {/* Left */}
            <div className="topics-col">
              {/* Changes requested feedback */}
              {status === 'changes_requested' && (
                <div className="changes-banner">
                  <span className="changes-banner-emoji">✏️</span>
                  <div>
                    <p className="changes-banner-title">Our team asked for some changes</p>
                    <p className="changes-banner-note">{reviewNote || 'Please review your submitted tasks and resubmit.'}</p>
                  </div>
                </div>
              )}

              {/* Git Push Guide — big, shown before the task list so it's never missed */}
              {topics.length > 0 && (
                <button className="git-push-cta" onClick={() => setShowGitPush(true)}>
                  <span className="git-push-cta-icon">🚀</span>
                  <span className="git-push-cta-text">
                    <span className="git-push-cta-title">New to Git &amp; GitHub?</span>
                    <span className="git-push-cta-sub">Learn how to push your code — step by step guide</span>
                  </span>
                  <span className="git-push-cta-arrow">→</span>
                </button>
              )}

              {/* Progress Bar */}
              {!alreadySubmitted && topics.length > 0 && (
                <ProgressBar completed={completedCount} total={topics.length} />
              )}

              <div className="topics-heading-row">
                <p className="topics-heading">{topics.length} Task{topics.length !== 1 ? 's' : ''} · {courseName}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="view-rules-btn" onClick={() => setShowRules(true)}>📋 Rules</button>
                </div>
              </div>

              <div className="topics-stack">
                {topics.length > 0 ? (() => {
                  const modules = MODULE_COURSE_MAP[courseName];
                  if (modules) {
                    // Render with module headers
                    let globalIdx = 0;
                    return modules.map((mod) => (
                      <div key={mod.module} className="module-group">
                        <div className="module-header">
                          <span className="module-title">Module {mod.module}: {mod.title}</span>
                          <span className="module-pill">{mod.topics.length} task{mod.topics.length !== 1 ? 's' : ''}</span>
                        </div>
                        {mod.topics.map((topic) => {
                          const gi = globalIdx++;
                            // Retrieve initial screenshots array from window cache set earlier
                            const initScreens = (window.__micro_ss__ && window.__micro_ss__[id]) ? (window.__micro_ss__[id][gi] || []) : [];
                            return (
                              <TopicCard
                                key={topic.id}
                                topic={topic}
                                index={gi}
                                globalIndex={gi}
                                totalTopics={topics.length}
                                status={status}
                                isCompleted={completedTopics[gi] || false}
                                githubLink={topicLinks[gi] || ''}
                                onLinkChange={val => handleLinkChange(gi, val)}
                                onMarkComplete={() => handleMarkComplete(gi)}
                                onEditAgain={() => handleEditAgain(gi)}
                                onShowGitPush={() => setShowGitPush(true)}
                                internshipId={id}
                                email={email}
                                initialScreenshots={initScreens}
                              />
                            );
                        })}
                      </div>
                    ));
                  }
                  // Fallback: flat render for courses without modules
                  return topics.map((topic, i) => (
                    <TopicCard
                      key={topic.id}
                      topic={topic}
                      index={i}
                      totalTopics={topics.length}
                      status={status}
                      isCompleted={completedTopics[i] || false}
                      githubLink={topicLinks[i] || ''}
                      onLinkChange={val => handleLinkChange(i, val)}
                      onMarkComplete={() => handleMarkComplete(i)}
                      onEditAgain={() => handleEditAgain(i)}
                      onShowGitPush={() => setShowGitPush(true)}
                      internshipId={id}
                      email={email}
                    />
                  ));
                })() : (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>
                    Task content for <strong>"{courseName}"</strong> is coming soon!
                  </div>
                )}
              </div>

              {/* Final Submit Button */}
              {topics.length > 0 && !alreadySubmitted && (
                <div className="final-submit-wrap">
                  <button
                    className="final-submit-btn"
                    disabled={!allDone || submitting}
                    onClick={handleFinalSubmit}
                  >
                    {submitting ? 'Submitting…' : allDone ? '🚀 Submit Final Project for Review' : `Complete all ${topics.length} tasks to submit`}
                  </button>
                  <p className="final-submit-note">
                    {allDone
                      ? 'All tasks completed! Click above to submit for admin review.'
                      : `${completedCount} of ${topics.length} tasks completed — finish all to enable submission.`}
                  </p>
                </div>
              )}

              {/* Already submitted state */}
              {alreadySubmitted && (
                <div className="submitted-info" style={{ marginTop: 12, borderRadius: 12, padding: '16px 20px' }}>
                  <span style={{ fontSize: 24 }}>{status === 'completed' ? '🏆' : '✅'}</span>
                  <div>
                    <p style={{ fontWeight: 700, marginBottom: 4, fontSize: 15 }}>
                      {status === 'completed' ? 'Approved — Certificate Issued!' : 'Project Submitted — Under Review'}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {status === 'under_review' ? 'Our team is reviewing your work. This usually takes 24–48 hours.' : 'Great job! Check your certificate below.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <StatusSidebar
              status={status}
              courseName={courseName}
              name={name}
              id={id}
              completedCount={completedCount}
              totalTopics={topics.length}
            />
          </div>
        )}

        {/* Certificate Section */}
        {status !== 'pending_payment' && (
          <CertificateSection status={status} certificateUrl={certificateUrl} certificateFormat={certificateFormat} internshipId={id} />
        )}

      </div>

      {showPanel      && <StatusPanel status="under_review" onClose={() => setShowPanel(false)} />}
      {showRules      && <RulesModal courseName={courseName} onClose={() => setShowRules(false)} />}
      {showGitPush    && <GitPushModal onClose={() => setShowGitPush(false)} />}
      {showMotivation !== null && <MotivationModal topic={showMotivation} onClose={() => setShowMotivation(null)} />}
    </>
  );
};

export default MicrointernshipTask;
